import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card } from "@/hq/HQPrimitives";
import { asText } from "@/hq/asText";
import PitchDetailModal from "@/hq/campaigns/PitchDetailModal";
import InterestedProspectsDrawer, { labelForPath, ExternalPageLink } from "@/hq/dashboard/InterestedProspectsDrawer";
import {
  CountUp, ProgressRing, ConversionRings, DonutChart, BarChartH,
  LineArea, Sparkline, LivePulse,
} from "@/hq/dashboard/charts";
import {
  Send, Mail, MousePointerClick, CheckCircle2, TrendingUp, Users,
  Target, Zap, Globe, ArrowUpRight, Sparkles, ArrowRight,
} from "lucide-react";

/**
 * Phase 3I.4R — Orange HQ Command Centre.
 *
 * Single-page premium analytics dashboard. Reads `/control/dashboard/summary`
 * (one aggregated call) plus opt-in secondary endpoints. Every number
 * comes from canonical Q.1-Q.4 sources — never fabricated.
 */

const CANONICAL_LABEL = {
  PITCH_SENT: "Pitch sent",
  PITCH_OPENED: "Pitch opened",
  PITCH_CLICKED: "Pitch clicked",
  PITCH_INTERESTED: "Marked Interested",
  PITCH_INTERESTED_CLEARED: "Interested cleared",
  CAMPAIGN_SENT: "Campaign sent",
  CAMPAIGN_OPENED: "Campaign opened",
  CAMPAIGN_CLICKED: "Campaign clicked",
  CAMPAIGN_UNSUBSCRIBED: "Unsubscribed",
};

const CANONICAL_TONE = {
  PITCH_INTERESTED: "#0F7B3E",
  PITCH_CLICKED:    "#FF6A00",
  PITCH_OPENED:     "#8A4A00",
  PITCH_SENT:       "#4A4A4A",
  CAMPAIGN_CLICKED: "#FF6A00",
  CAMPAIGN_OPENED:  "#8A4A00",
  CAMPAIGN_SENT:    "#4A4A4A",
  CAMPAIGN_UNSUBSCRIBED: "#C1350B",
};

const fmtMoneyMinor = (minor, currency = "AED") => {
  const n = (Number(minor) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(n);
  } catch (_) { return `${currency} ${n.toLocaleString()}`; }
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const now = new Date();
  const s = Math.max(1, Math.floor((now - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const humanServiceKey = (k) => {
  const m = { crm: "CRM", website: "Websites", orangecare: "OrangeCare",
              orange_optimise: "Orange Optimise", full_pitch: "Full Pitch",
              other: "Other", subscription: "Subscriptions" };
  return m[(k || "").toLowerCase()] || (k || "Other");
};

function EyebrowTitle({ eyebrow, title, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#FF6A00]">{eyebrow}</div>
        <h2 className="text-2xl md:text-3xl font-black tracking-[-0.02em] mt-1">{title}</h2>
      </div>
      {right}
    </div>
  );
}

function KpiTile({ eyebrow, value, suffix = "", sub, tone = "orange", spark, decimals = 0, testId,
                    onClick, secondary }) {
  const bg = tone === "orange" ? "linear-gradient(135deg,#FFF7EE 0%,#FFFFFF 60%)" :
             tone === "green"  ? "linear-gradient(135deg,#E9F7EF 0%,#FFFFFF 65%)" :
             tone === "dark"   ? "linear-gradient(135deg,#0A0A0A 0%,#1B1B1B 100%)" :
                                  "#FFFFFF";
  const fg = tone === "dark" ? "text-white" : "text-[#0A0A0A]";
  const eyebrowColor = tone === "dark"  ? "text-[#FF6A00]"
                       : tone === "green" ? "text-[#0F7B3E]"
                                          : "text-[#8A2E00]";
  const borderColor = tone === "green" ? "border-[#B8DFC4]" : "border-[#F0EBE3]";
  const hoverShadow = tone === "green"
    ? "hover:shadow-[0_4px_12px_rgba(15,123,62,0.18)] hover:border-[#0F7B3E]"
    : "hover:shadow-[0_4px_12px_rgba(255,106,0,0.15)] hover:border-[#FF6A00]";
  const clickable = typeof onClick === "function";
  const Wrap = clickable ? "button" : "div";
  return (
    <Wrap data-testid={testId}
         onClick={clickable ? onClick : undefined}
         type={clickable ? "button" : undefined}
         className={`relative rounded-2xl p-4 md:p-5 border ${borderColor} shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${fg} ${clickable ? `text-left ${hoverShadow} transition-all cursor-pointer` : ""}`}
         style={{ background: bg }}>
      <div className={`text-[10px] tracking-[0.24em] uppercase font-black ${eyebrowColor} flex items-center gap-1`}>
        {eyebrow}
        {clickable && <ArrowUpRight size={11} className="opacity-60"/>}
      </div>
      <div className="mt-1 text-3xl md:text-4xl font-black tabular-nums leading-none">
        <CountUp value={value} suffix={suffix} decimals={decimals}/>
      </div>
      {secondary && (
        <div className={`text-[11px] mt-1 font-bold ${
          tone === "dark"  ? "text-[#FF6A00]"
          : tone === "green" ? "text-[#0F7B3E]"
                             : "text-[#0F7B3E]"}`}>
          {secondary}
        </div>
      )}
      {sub && <div className={`text-[11px] mt-1 ${tone === "dark" ? "text-[#B8B8B8]" : "text-[#8A8A8A]"}`}>{sub}</div>}
      {spark?.length ? <div className="mt-2 opacity-70"><Sparkline data={spark}/></div> : null}
    </Wrap>
  );
}

export default function HQCommandCentre() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [selectedComm, setSelectedComm] = useState(null);
  const [drawer, setDrawer] = useState(null); // {variant, stage?, days?}
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    hqApi
      .get("/control/dashboard/summary", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [days]);

  if (!data) {
    return (
      <div className="w-full p-4 md:p-6 space-y-6" data-testid="hq-command-centre-loading">
        <div className="h-16 rounded-2xl bg-[#F6F5F2] animate-pulse"/>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-[#F6F5F2] animate-pulse"/>)}
        </div>
        <div className="h-96 rounded-2xl bg-[#F6F5F2] animate-pulse"/>
      </div>
    );
  }
  if (data.error) {
    return <div className="p-6 text-[#C1350B]">Failed to load dashboard.</div>;
  }

  const exec = data.executive || {};
  const pp = data.pitch_performance || {};
  const targets = data.targets || {};
  const currency = targets.currency || "AED";
  const targetMinor = targets.revenue_target_minor || 0;
  const revenueMinor = (data.revenue?.actual_by_currency || {})[currency] || 0;
  const pipelineMinor = (data.revenue?.pipeline_by_currency || {})[currency] || 0;
  const remainingMinor = Math.max(0, targetMinor - revenueMinor);
  const pctTarget = targetMinor ? Math.min(100, (revenueMinor / targetMinor) * 100) : 0;

  // Build revenue mix chart
  const mixByService = {};
  (data.revenue?.mix || []).forEach((r) => {
    const k = r.service_key || "other";
    mixByService[k] = (mixByService[k] || 0) + (r.amount_minor || 0);
  });
  const mixColors = { crm: "#FF6A00", website: "#FFA25C", orangecare: "#8A2E00",
                       orange_optimise: "#0F7B3E", other: "#B8B8B8" };
  const mixData = Object.entries(mixByService).map(([k, v]) => ({
    label: humanServiceKey(k), value: v, color: mixColors[k] || "#B8B8B8",
  })).sort((a, b) => b.value - a.value);

  // Pitch breakdown for horizontal bars
  const breakdownData = (pp.breakdown || []).map((r) => ({
    label: r.pitch_type === "full" ? "Full Pitch" :
            r.pitch_type.charAt(0).toUpperCase() + r.pitch_type.slice(1),
    value: r.sent, opened: r.opened, clicked: r.clicked, interested: r.interested,
  }));

  // Series for line chart
  const series = pp.series || [];

  // Funnel data
  const funnel = data.funnel || {};
  const funnelStages = [
    { key: "prospects_total", label: "Prospects", value: exec.prospects || 0, color: "#0A0A0A" },
    { key: "pitched",         label: "Pitched",   value: pp.sent || 0,        color: "#4A4A4A" },
    { key: "opened",          label: "Opened",    value: pp.opened || 0,      color: "#8A4A00" },
    { key: "clicked",         label: "Clicked",   value: pp.clicked || 0,     color: "#FF6A00" },
    { key: "interested",      label: "Interested", value: pp.interested || 0, color: "#8A2E00" },
    { key: "NEGOTIATION",     label: "Negotiation", value: funnel.NEGOTIATION || 0, color: "#0F7B3E" },
    { key: "WON",             label: "Won",        value: funnel.WON || 0, color: "#0F7B3E" },
  ];
  const funnelMax = Math.max(1, ...funnelStages.map((s) => s.value));

  // Target planner
  const remainingMajor = remainingMinor / 100;
  const products = Object.entries(targets.product_values_minor || {}).map(([k, v]) => {
    const avg = (v || 0) / 100;
    const needed = avg > 0 ? Math.ceil(remainingMajor / avg) : 0;
    return { key: k, label: humanServiceKey(k), avg, needed };
  });

  const of = data.outreach_funnel || {};
  const fp = data.followup_performance || {};

  return (
    <div className="w-full p-4 md:p-6 space-y-6" data-testid="hq-command-centre">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00] flex items-center gap-2">
            <Sparkles size={12}/> ORANGE HQ · INTELLIGENCE
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-[-0.02em] mt-2" data-testid="hq-cc-title">
            Dashboard
          </h1>
          <p className="text-[13px] text-[#4A4A4A] mt-1">What's happening → what's working → where they're engaging → how close we are.</p>
        </div>
        <div className="flex gap-1 rounded-full bg-white border border-[#EAEAEA] p-1" data-testid="hq-cc-range">
          {[7, 30, 90, 365].map((d) => (
            <button key={d} data-testid={`hq-cc-range-${d}`} onClick={() => setDays(d)}
                    className={`px-3 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-black ${days === d ? "bg-[#0A0A0A] text-white" : "text-[#8A8A8A] hover:text-[#0A0A0A]"}`}>
              {d === 365 ? "1Y" : `${d}D`}
            </button>
          ))}
        </div>
      </div>

      {/* Executive KPI Strip — Phase 3I.4W.1:
           * Orange Leads REMOVED from row 1 (lives only in row 2). */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="hq-cc-executive">
        <KpiTile testId="hq-cc-kpi-prospects"    eyebrow="Prospects"       value={exec.prospects || 0}
                 sub={exec.clients ? `${exec.clients} clients` : null}/>
        <KpiTile testId="hq-cc-kpi-pitches"      eyebrow="Pitches sent"    value={exec.pitches_sent || 0} tone="orange"
                 sub={typeof exec.pitches_sent_unique_businesses === "number"
                      ? `${exec.pitches_sent_unique_businesses} unique businesses`
                      : null}/>
        <KpiTile testId="hq-cc-kpi-open-rate"    eyebrow="Open rate"       value={exec.open_rate || 0} suffix="%" decimals={1}/>
        <KpiTile testId="hq-cc-kpi-click-rate"   eyebrow="Click rate"      value={exec.click_rate || 0} suffix="%" decimals={1}/>
        <KpiTile testId="hq-cc-kpi-health-checks" eyebrow="Free health checks"
                 value={(data.health_checks?.total_all_time) || 0}
                 onClick={() => setDrawer({ variant: "health_checks", days })}
                 secondary={data.health_checks?.total_last_period ? `+${data.health_checks.total_last_period} last ${days === 365 ? "year" : `${days} days`}` : null}/>
        <KpiTile testId="hq-cc-kpi-revenue"      eyebrow={`Revenue · ${currency}`} value={revenueMinor / 100} tone="dark"
                 sub={remainingMinor ? `${fmtMoneyMinor(remainingMinor, currency)} to target` : "Target reached"}/>
      </div>

      {/* Phase 3I.4S/W — Follow-up performance strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hq-cc-followups">
        <KpiTile testId="hq-cc-kpi-fu-sent"       eyebrow="Follow-ups sent"      value={fp.sent || 0}
                 sub="communications"
                 onClick={() => setDrawer({ variant: "outreach:followups_sent", stage: "followups_sent", days })}/>
        <KpiTile testId="hq-cc-kpi-fu-opened"     eyebrow="Follow-ups opened"    value={fp.opened || 0}
                 sub="communications"
                 onClick={() => setDrawer({ variant: "outreach:followups_opened", stage: "followups_opened", days })}/>
        <KpiTile testId="hq-cc-kpi-fu-cta"        eyebrow="Follow-up CTA clicks" value={fp.cta_clicks || 0}
                 sub={typeof fp.cta_clicks_communications === "number"
                      ? `${fp.cta_clicks_communications} communications`
                      : null}
                 onClick={() => setDrawer({ variant: "outreach:followup_cta_clicks", stage: "followup_cta_clicks", days })}/>
        <KpiTile testId="hq-cc-kpi-orange-leads" eyebrow="Orange leads"
                 value={exec.orange_leads || 0} tone="green"
                 onClick={() => setDrawer({ variant: "orange_leads", source: "all", days })}
                 secondary={exec.orange_leads_last_period
                    ? `+${exec.orange_leads_last_period} last ${days === 365 ? "year" : `${days} days`}` : null}/>
      </div>

      {/* Phase 3I.4S/W — Outreach journey funnel (unique businesses only) */}
      <Card className="p-6" data-testid="hq-cc-outreach-funnel">
        <EyebrowTitle eyebrow="Outreach Journey" title="Pitch → Follow-up → Orange Leads"
          right={<div className="text-[11px] text-[#8A8A8A] tracking-[0.16em] uppercase font-bold">Last {days === 365 ? "year" : `${days} days`} · unique businesses per stage</div>}
        />
        {(() => {
          const stages = [
            { key: "pitches_sent",             label: "Businesses pitched",       tone: "#0A0A0A" },
            { key: "pitches_opened",           label: "Businesses opening pitch", tone: "#4A4A4A" },
            { key: "followups_sent",           label: "Businesses sent follow-up",   tone: "#FFA25C" },
            { key: "followups_opened",         label: "Businesses opening follow-up",tone: "#FF6A00" },
            { key: "followup_cta_clicks",      label: "Businesses clicking CTA",     tone: "#C1350B" },
            { key: "orange_leads_outreach",    label: "Orange leads (outreach)",     tone: "#0F7B3E" },
          ];
          const max = Math.max(1, ...stages.map((s) => of[s.key] || 0));
          return (
            <ul className="space-y-2 mt-2">
              {stages.map((s) => {
                const v = of[s.key] || 0;
                const pct = Math.round((v / max) * 100);
                return (
                  <li key={s.key}
                      data-testid={`hq-cc-outreach-${s.key}`}
                      className="flex items-center gap-3">
                    <button
                      type="button"
                      data-testid={`hq-cc-outreach-open-${s.key}`}
                      onClick={() => setDrawer(
                        s.key === "orange_leads_outreach"
                          ? { variant: "orange_leads", source: "outreach", days }
                          : { variant: `outreach:${s.key}`, stage: s.key, days })}
                      className="w-40 shrink-0 text-left text-[11px] uppercase tracking-[0.14em] font-bold text-[#4A4A4A] hover:text-[#FF6A00] hover:underline"
                    >
                      {s.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDrawer(
                        s.key === "orange_leads_outreach"
                          ? { variant: "orange_leads", source: "outreach", days }
                          : { variant: `outreach:${s.key}`, stage: s.key, days })}
                      className="flex-1 h-6 rounded-full bg-[#F6F5F2] overflow-hidden hover:opacity-90"
                      aria-label={`Open ${s.label} drill-down`}
                    >
                      <div className="h-full rounded-full transition-all duration-500"
                           style={{ width: `${pct}%`, background: s.tone }}/>
                    </button>
                    <div className="w-14 text-right tabular-nums font-black" style={{ color: s.tone }}>
                      {v}
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </Card>

      {/* Phase 3I.4V — Free Health Check panel (only real production data). */}
      {(() => {
        const hc = data.health_checks || {};
        return (
          <Card className="p-6" data-testid="hq-cc-health-checks">
            <EyebrowTitle
              eyebrow="Lead Generation · Free Health Check"
              title="What visitors are scanning"
              right={<div className="text-[11px] text-[#8A8A8A] tracking-[0.16em] uppercase font-bold">Public scans only · test & failed excluded</div>}
            />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <KpiTile testId="hq-cc-hc-total"      eyebrow="Total"        value={hc.total_all_time || 0}
                       onClick={() => setDrawer({ variant: "health_checks", days: 0 })}/>
              <KpiTile testId="hq-cc-hc-7d"         eyebrow="Last 7 days"  value={hc.total_7d || 0}/>
              <KpiTile testId="hq-cc-hc-30d"        eyebrow="Last 30 days" value={hc.total_30d || 0}/>
              <KpiTile testId="hq-cc-hc-avg-score"  eyebrow="Avg orange score" value={hc.avg_orange_score || 0} decimals={1}/>
              <KpiTile testId="hq-cc-hc-low-score"  eyebrow="Lowest score" value={hc.lowest_orange_score ?? 0}/>
              <KpiTile testId="hq-cc-hc-qualified"  eyebrow="Qualified leads (score ≤ 60)"
                       value={hc.qualified_leads || 0} tone="orange"/>
            </div>
            <div className="mt-6">
              <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-3">Funnel</div>
              {(() => {
                const stages = [
                  { key: "total_all_time", label: "Health Checks", tone: "#0A0A0A" },
                  { key: "qualified_leads", label: "Qualified (score ≤ 60)", tone: "#FF6A00" },
                ];
                const max = Math.max(1, ...stages.map((s) => hc[s.key] || 0));
                return (
                  <ul className="space-y-2">
                    {stages.map((s) => {
                      const v = hc[s.key] || 0;
                      const pct = Math.round((v / max) * 100);
                      return (
                        <li key={s.key} className="flex items-center gap-3"
                            data-testid={`hq-cc-hc-funnel-${s.key}`}>
                          <div className="w-56 shrink-0 text-[11px] uppercase tracking-[0.14em] font-bold text-[#4A4A4A]">
                            {s.label}
                          </div>
                          <div className="flex-1 h-6 rounded-full bg-[#F6F5F2] overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500"
                                 style={{ width: `${pct}%`, background: s.tone }}/>
                          </div>
                          <div className="w-14 text-right tabular-nums font-black" style={{ color: s.tone }}>
                            {v}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
              <p className="text-[11px] text-[#8A8A8A] mt-3">
                Prospects / Orange Optimise / Orange Fix / Client conversion stages
                require a canonical <span className="font-black">scan_id → prospect_id → order_id</span> link
                that isn't yet tracked in production. Reported for follow-up work.
              </p>
            </div>
          </Card>
        );
      })()}

      {/* Pitch Performance — main visual */}
      <Card className="p-6" data-testid="hq-cc-pitch-performance">
        <EyebrowTitle eyebrow="Pitch Performance" title="Conversion & engagement"
          right={<div className="text-[11px] text-[#8A8A8A] tracking-[0.16em] uppercase font-bold">Last {days === 365 ? "year" : `${days} days`}</div>}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="flex flex-col items-center justify-center gap-3">
            <ConversionRings sent={pp.sent || 0} opened={pp.opened || 0}
                              clicked={pp.clicked || 0} interested={pp.interested || 0}
                              testId="hq-cc-conversion-rings"/>
            <div className="grid grid-cols-3 gap-2 text-center text-[11px] w-full">
              <div><div className="tabular-nums font-black text-[#FF6A00]">{pp.open_rate || 0}%</div><div className="text-[#8A8A8A] uppercase tracking-[0.16em] text-[9px]">Open</div></div>
              <div><div className="tabular-nums font-black text-[#FFA25C]">{pp.click_rate || 0}%</div><div className="text-[#8A8A8A] uppercase tracking-[0.16em] text-[9px]">Click</div></div>
              <div><div className="tabular-nums font-black text-[#0F7B3E]">{pp.interested_rate || 0}%</div><div className="text-[#8A8A8A] uppercase tracking-[0.16em] text-[9px]">Interested</div></div>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl p-3 bg-[#FFF7EE] border border-[#FCE1C7]">
                <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A2E00] font-black">Opens · 24h</div>
                <div className="text-2xl font-black tabular-nums"><CountUp value={pp.opens_24h || 0}/></div>
              </div>
              <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
                <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Opens · 7d</div>
                <div className="text-2xl font-black tabular-nums"><CountUp value={pp.opens_7d || 0}/></div>
              </div>
              <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
                <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Clicks · 7d</div>
                <div className="text-2xl font-black tabular-nums"><CountUp value={pp.clicks_7d || 0}/></div>
              </div>
              <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
                <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Total events</div>
                <div className="text-2xl font-black tabular-nums"><CountUp value={(pp.total_open_events || 0) + (pp.total_click_events || 0)}/></div>
                <div className="text-[10px] text-[#8A8A8A]">Opens {pp.total_open_events || 0} · Clicks {pp.total_click_events || 0}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-2">Engagement over time</div>
              <LineArea series={series}
                        keys={["sent", "opened", "clicked", "interested"]}
                        colors={["#4A4A4A", "#FFA25C", "#FF6A00", "#0F7B3E"]}
                        testId="hq-cc-pitch-series"/>
              <div className="flex flex-wrap gap-4 text-[10px] mt-2 tracking-[0.16em] uppercase font-bold text-[#8A8A8A]">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4A4A4A]"/> Sent</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FFA25C]"/> Opened</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF6A00]"/> Clicked</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0F7B3E]"/> Interested</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-3">By pitch type</div>
            <BarChartH data={breakdownData} testId="hq-cc-pitch-type-bars"/>
          </div>
          <div className="space-y-3">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-1">Standout signals</div>
            {pp.most_opened ? (
              <div className="p-3 rounded-xl bg-[#FFF9E6] border border-[#F5E4B0] cursor-pointer hover:bg-[#FFF7DA]"
                   onClick={() => setSelectedComm(pp.most_opened.id)}
                   data-testid="hq-cc-most-opened">
                <div className="text-[9px] tracking-[0.28em] uppercase font-black text-[#8A6D00]">Most opened</div>
                <div className="font-black text-[14px]">{asText(pp.most_opened.business_name) || "—"}</div>
                <div className="text-[11px] text-[#8A8A8A]">{pp.most_opened.open_count} opens · {pp.most_opened.pitch_type}</div>
              </div>
            ) : (<div className="p-3 rounded-xl bg-[#F6F5F2] text-[12px] text-[#8A8A8A]">Awaiting first open event.</div>)}
            {pp.most_clicked ? (
              <div className="p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7] cursor-pointer hover:bg-[#FFECD8]"
                   onClick={() => setSelectedComm(pp.most_clicked.id)}
                   data-testid="hq-cc-most-clicked">
                <div className="text-[9px] tracking-[0.28em] uppercase font-black text-[#8A2E00]">Most clicked</div>
                <div className="font-black text-[14px]">{asText(pp.most_clicked.business_name) || "—"}</div>
                <div className="text-[11px] text-[#8A8A8A]">{pp.most_clicked.click_count} clicks · {pp.most_clicked.pitch_type}</div>
              </div>
            ) : (<div className="p-3 rounded-xl bg-[#F6F5F2] text-[12px] text-[#8A8A8A]">Awaiting first click.</div>)}
          </div>
        </div>
      </Card>

      {/* Website Traffic */}
      <Card className="p-6" data-testid="hq-cc-website">
        <EyebrowTitle eyebrow="Live · Website Traffic" title="Digital footfall"
          right={<LivePulse testId="hq-cc-website-live"/>}/>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="rounded-xl p-3 bg-[#EBFBF2] border border-[#B7EBCF]" data-testid="hq-cc-visitors-now">
            <div className="text-[9px] tracking-[0.28em] uppercase text-[#0F7B3E] font-black">Right now</div>
            <div className="text-3xl font-black text-[#0F7B3E] tabular-nums"><CountUp value={data.website?.live_visitors || 0}/></div>
          </div>
          <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
            <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Today · visitors</div>
            <div className="text-2xl font-black tabular-nums"><CountUp value={data.website?.today_visitors || 0}/></div>
          </div>
          <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
            <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Today · views</div>
            <div className="text-2xl font-black tabular-nums"><CountUp value={data.website?.today_views || 0}/></div>
          </div>
          <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
            <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">7d · visitors</div>
            <div className="text-2xl font-black tabular-nums"><CountUp value={data.website?.week_visitors || 0}/></div>
          </div>
          <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
            <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">7d · views</div>
            <div className="text-2xl font-black tabular-nums"><CountUp value={data.website?.week_views || 0}/></div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-2">Traffic timeline</div>
            <LineArea series={(data.website?.series || []).map((r) => ({ day: r.day, views: r.views, visitors: r.visitors }))}
                      keys={["views", "visitors"]}
                      colors={["#FF6A00", "#0A0A0A"]}
                      testId="hq-cc-traffic-series"/>
            <div className="flex flex-wrap gap-4 text-[10px] mt-2 tracking-[0.16em] uppercase font-bold text-[#8A8A8A]">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF6A00]"/> Views</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0A0A0A]"/> Visitors</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-2">Top pages · public</div>
            {(() => {
              /* Phase 3I.4U — Top pages: human labels, external-link icon,
                 open in new tab; /interest/* excluded upstream. */
              const pages = (data.website?.top_pages || []);
              if (pages.length === 0) {
                return <div className="text-[12px] text-[#8A8A8A]">No public page views yet.</div>;
              }
              const max = Math.max(1, ...pages.map((p) => p.views));
              return (
                <ul className="space-y-2" data-testid="hq-cc-top-pages">
                  {pages.map((p, i) => {
                    const label = labelForPath(p.path);
                    return (
                      <li key={i} className="flex items-center gap-3"
                          data-testid={`hq-cc-top-page-${i}`}>
                        <div className="flex-1 min-w-0">
                          <ExternalPageLink path={p.path} testId={`hq-cc-top-page-link-${i}`}/>
                        </div>
                        <div className="flex-1 h-2 rounded-full bg-[#F6F5F2] overflow-hidden max-w-[120px]">
                          <div className="h-full rounded-full bg-[#FF6A00]"
                               style={{ width: `${Math.round((p.views / max) * 100)}%` }}/>
                        </div>
                        <div className="w-10 text-right tabular-nums font-black text-[13px]">
                          {p.views}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
            <div className="mt-4 text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-2">Recent footfall</div>
            <ul className="text-[11px] font-mono divide-y divide-[#F0EBE3]" data-testid="hq-cc-recent-footfall">
              {(data.website?.recent || []).slice(0, 8).map((r, i) => (
                <li key={i} className="flex justify-between py-1">
                  <span className="text-[#8A8A8A]">{new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="truncate ml-2">{asText(r.path)}</span>
                </li>
              ))}
              {(data.website?.recent || []).length === 0 && <li className="text-[#8A8A8A] py-1">No recent visits.</li>}
            </ul>
          </div>
        </div>
      </Card>

      {/* Road to Target */}
      <Card className="p-6" data-testid="hq-cc-target">
        <EyebrowTitle eyebrow="Growth" title={`Road to ${fmtMoneyMinor(targetMinor, currency)}`}/>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="flex flex-col items-center">
            <ProgressRing value={pctTarget} size={220} stroke={18}
                           label={`${pctTarget.toFixed(1)}%`}
                           sub={targets.period_label || "THIS YEAR"}
                           testId="hq-cc-target-ring"/>
            <div className="mt-3 text-center">
              <div className="text-3xl font-black tabular-nums">{fmtMoneyMinor(revenueMinor, currency)}</div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold mt-1">
                of {fmtMoneyMinor(targetMinor, currency)}
              </div>
              <div className="text-[12px] text-[#8A8A8A] mt-2">{fmtMoneyMinor(remainingMinor, currency)} remaining</div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#8A8A8A] mb-3">What gets us there?</div>
            <div className="space-y-3" data-testid="hq-cc-target-planner">
              {products.map((p) => (
                <div key={p.key} className="grid grid-cols-[1fr_2fr_auto] items-center gap-3 text-[13px]">
                  <div className="font-black">{p.label}</div>
                  <div className="h-2 rounded-full bg-[#F0EBE3] overflow-hidden">
                    <div className="h-full bg-[#FF6A00]" style={{ width: p.needed > 0 && p.avg > 0 ? "100%" : "0%",
                                                                    transition: "width 700ms cubic-bezier(0.22,1,0.36,1)" }}/>
                  </div>
                  <div className="tabular-nums text-[#8A8A8A] font-bold whitespace-nowrap">
                    ~ {p.needed.toLocaleString()} @ {fmtMoneyMinor(p.avg * 100, currency)}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-[#8A8A8A] mt-2">Configure average sale values under HQ → Settings → Business Targets.</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Sales Pipeline + Revenue Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="hq-cc-pipeline">
          <EyebrowTitle eyebrow="Pipeline" title="Prospects → Won"/>
          <BarChartH
            data={funnelStages.map((s) => ({ label: s.label, value: s.value, color: s.color }))}
            max={funnelMax}
            testId="hq-cc-funnel-bars"
          />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
              <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Actual revenue</div>
              <div className="text-2xl font-black tabular-nums">{fmtMoneyMinor(revenueMinor, currency)}</div>
            </div>
            <div className="rounded-xl p-3 bg-white border border-[#EAEAEA]">
              <div className="text-[9px] tracking-[0.28em] uppercase text-[#8A8A8A] font-black">Pipeline potential</div>
              <div className="text-2xl font-black tabular-nums text-[#FF6A00]">{fmtMoneyMinor(pipelineMinor, currency)}</div>
              <div className="text-[10px] text-[#8A8A8A]">Open opportunities · never counted as revenue</div>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-testid="hq-cc-revenue-mix">
          <EyebrowTitle eyebrow="Revenue" title="Revenue mix"/>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <DonutChart data={mixData} testId="hq-cc-revenue-donut"/>
            <div className="flex-1 space-y-2 w-full">
              {mixData.length === 0 ? (
                <div className="text-[12px] text-[#8A8A8A]">Revenue mix appears once paid invoices exist.</div>
              ) : mixData.map((d, i) => (
                <div key={i} className="flex items-center gap-3 text-[12px]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }}/>
                  <span className="flex-1 font-bold truncate">{d.label}</span>
                  <span className="tabular-nums text-[#8A8A8A]">{fmtMoneyMinor(d.value, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6" data-testid="hq-cc-recent-activity">
        <EyebrowTitle eyebrow="Live · Feed" title="Recent activity"
          right={<button className="text-[11px] tracking-[0.16em] uppercase font-black text-[#FF6A00] hover:underline"
                          onClick={() => navigate("/hq/campaigns")}
                          data-testid="hq-cc-view-activity">View full activity <ArrowRight size={11} className="inline"/></button>}
        />
        {(data.recent_activity || []).length === 0 ? (
          <div className="text-[12px] text-[#8A8A8A]">No pitch or campaign activity yet.</div>
        ) : (
          <ul className="divide-y divide-[#F0EBE3]" data-testid="hq-cc-recent-list">
            {(data.recent_activity || []).map((a) => {
              const tone = CANONICAL_TONE[a.canonical_type] || "#4A4A4A";
              const hasClient = !!a.business_id;
              const hasPitch  = !!a.communication_id;
              return (
                <li key={a.id}
                    data-testid={`hq-cc-recent-row-${a.id}`}
                    className="py-2 flex items-start gap-3 px-2 -mx-2 rounded-lg hover:bg-[#FFF7EE]">
                  <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: tone }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px]">
                      {hasClient ? (
                        <button
                          type="button"
                          data-testid={`hq-cc-recent-business-${a.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/hq/clients/${a.business_id}`); }}
                          className="font-black hover:text-[#FF6A00] hover:underline"
                        >{asText(a.business_name) || "—"}</button>
                      ) : (
                        <span className="font-black">{asText(a.business_name) || "—"}</span>
                      )}{" "}
                      {hasPitch ? (
                        <button
                          type="button"
                          data-testid={`hq-cc-recent-event-${a.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedComm(a.communication_id); }}
                          className="text-[#8A8A8A] hover:text-[#0A0A0A] hover:underline"
                        >{CANONICAL_LABEL[a.canonical_type] || asText(a.event_type)}</button>
                      ) : (
                        <span className="text-[#8A8A8A]">{CANONICAL_LABEL[a.canonical_type] || asText(a.event_type)}</span>
                      )}
                    </div>
                    {a.description && <div className="text-[11px] text-[#8A8A8A] truncate">{asText(a.description)}</div>}
                  </div>
                  <div className="text-[11px] text-[#8A8A8A] shrink-0">{fmtRelative(a.created_at)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <PitchDetailModal commId={selectedComm} onClose={() => setSelectedComm(null)}/>
      <InterestedProspectsDrawer
        open={!!drawer}
        variant={drawer?.variant || "all"}
        stage={drawer?.stage || null}
        days={drawer?.days || 0}
        source={drawer?.source || "all"}
        onClose={() => setDrawer(null)}
        onOpenPitch={(cid) => { setDrawer(null); setSelectedComm(cid); }}
      />
    </div>
  );
}
